import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Search, Car } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import {
    availableVehiclesKey,
    fetchAvailableVehicles,
    type AvailableVehicle as Vehicle,
} from '@/components/contracts/setup/data/vehicles';

interface SelectVehicleStepProps {
    selectedVehicle: Vehicle | null;
    onSelect: (vehicle: Vehicle | null) => void;
}

export default function SelectVehicleStep({
    selectedVehicle,
    onSelect,
}: SelectVehicleStepProps) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);

    /**
     * ONE FETCHER FOR ONE KEY. The body of this query moved to
     * `setup/data/vehicles.ts` when the setup canvas grew a change-vehicle picker: a second query
     * function registered against `['available-vehicles', …]` is how two shapes end up sharing a
     * cache entry and whichever mounted first wins. Behaviour here is unchanged.
     */
    const { data: vehicles, isLoading } = useQuery({
        queryKey: availableVehiclesKey(debouncedSearch),
        queryFn: () => fetchAvailableVehicles(debouncedSearch),
    });

    const getVehicleDisplay = (vehicle: Vehicle) => {
        return vehicle.registration_number || vehicle.vin || `${vehicle.make} ${vehicle.model}`;
    };

    return (
        <div className="h-full flex flex-col space-y-4 p-1">
            {/* Fixed Header Section */}
            <div className="flex-none space-y-4">
                {/* Search input */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by registration, VIN, make, or model..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                    />
                </div>

                {/* Selected vehicle card */}
                {selectedVehicle && (
                    <Card className="border-primary bg-primary/5">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                                    <Car className="h-6 w-6 text-muted-foreground" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium">
                                        {getVehicleDisplay(selectedVehicle)}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {selectedVehicle.make} {selectedVehicle.model}
                                    </p>
                                </div>
                                <span className="text-xs text-primary font-medium">Selected</span>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Scrollable Vehicle list */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-2">
                {isLoading ? (
                    [...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))
                ) : vehicles?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        <Car className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>No available vehicles found</p>
                    </div>
                ) : (
                    vehicles?.map((vehicle: Vehicle) => (
                        <Card
                            key={vehicle.id}
                            className={`cursor-pointer transition-colors hover:bg-muted/50 ${selectedVehicle?.id === vehicle.id ? 'border-primary bg-primary/5' : ''
                                }`}
                            onClick={() => onSelect(vehicle)}
                        >
                            <CardContent className="p-3">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                                        <Car className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">
                                            {getVehicleDisplay(vehicle)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {vehicle.make} {vehicle.model}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
}
